import { PureComponent, ReactNode } from 'react';
import { Service } from '../../../../../api/getTreatmentPlans';
import { columnHeaderContent, headerStyle } from '../../../../common/TableUtils';
import { Input, Table, notification } from 'antd';
import Column from 'antd/lib/table/Column';
import AntSelectWithLabel from '../padV2/AntSelectWithLabel';
import {
    allSurfaces,
    allTeethData,
    allTypeUniqueTeeth,
} from '../../../leftPanel/DentalChart/constants';
import { Surface } from '../../../../../store/types/prescription';
import { EditableCellV2 } from '../padV2/MedTableRenderer';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../../store/reducers';
import { getChartType } from '../../../../../store/selectors/prescriptionPad';
import { ChartType } from '../../../leftPanel/DentalChart/DentalChart';
import { formattedNumber } from '../../../../../pages/utils';

interface Props {
    services: Service[];
    updateService: (newService: Service, index: number) => void;
    deleteService: (index: number) => void;
    chartType: ChartType;
}

class EstimateServicesTable extends PureComponent<Props> {
    seqHeaderContent = columnHeaderContent('');
    nameHeaderContent = columnHeaderContent('Services');
    teethHeaderContent = columnHeaderContent('Teeth');
    regionHeaderContent = columnHeaderContent('Region');
    qtyHeaderContent = columnHeaderContent('Qty');
    mrpHeaderContent = columnHeaderContent('Mrp');
    discountHeaderContent = columnHeaderContent('Discount (%)');
    amountHeaderContent = columnHeaderContent('Amount');
    remarksHeaderContent = columnHeaderContent('Remarks');
    actionHeaderContent = columnHeaderContent('');

    renderSeq = (_: Service, __: Service, index: number) => {
        return <p>{index + 1}</p>;
    };

    renderName = (service: Service) => {
        return <p>{service.name}</p>;
    };

    renderTeeth = (service: Service, __: Service, index: number) => {
        const { updateService, chartType } = this.props;

        return (
            <AntSelectWithLabel
                className="w-full"
                isMulti={true}
                showSearch={true}
                allowClear
                placeholder="Select teeth"
                value={service.teeth_ids?.map((s) => ({ id: s, name: s }))}
                notFoundContent={null}
                options={chartType ? allTeethData(chartType) : allTypeUniqueTeeth}
                getValue={(o) => o?.id}
                getLabelToCompare={(o) => o.name}
                setValue={(t) => {
                    const teethIds = t?.map((item) => item.id) || [];
                    const { qty, mrp, amount } = service;
                    const actualMrp = mrp / qty;
                    const actualAmount = amount / qty;
                    const newQty = teethIds.length || 1;
                    updateService(
                        {
                            ...service,
                            teeth_ids: teethIds,
                            qty: newQty,
                            mrp: newQty * actualMrp,
                            amount: newQty * actualAmount,
                        },
                        index,
                    );
                }}
                filterOption={true}
                optionFilterProp="name"
            />
        );
    };

    renderRegion = (service: Service, __: Service, index: number) => {
        const { updateService } = this.props;

        return (
            <AntSelectWithLabel
                className="w-full"
                isMulti={true}
                showSearch={true}
                allowClear
                placeholder="Select area"
                value={service.surfaces}
                notFoundContent={null}
                options={allSurfaces}
                getValue={(o) => o?.id}
                getLabelToCompare={(o) => o.name}
                setValue={(t) => {
                    updateService(
                        {
                            ...service,
                            surfaces: t as Surface[],
                        },
                        index,
                    );
                }}
                filterOption={true}
                optionFilterProp="name"
            />
        );
    };

    renderQty = (service: Service, __: Service, index: number) => {
        const { updateService } = this.props;

        return {
            children: (
                <EditableCellV2
                    fieldName={`treatment-service-qty-${index}`}
                    defaultValue={String(service.qty) || ''}
                    onBlur={(e) => {
                        const { qty, mrp, amount } = service;
                        const actualMrp = mrp / qty;
                        const actualAmount = amount / qty;
                        const newQty = Number(e.target.value);
                        if (newQty <= 0) {
                            notification.error({
                                message: `Quantity must be greater than 0`,
                                duration: 5,
                            });
                            return;
                        }
                        updateService(
                            {
                                ...service,
                                qty: newQty,
                                mrp: newQty * actualMrp,
                                amount: newQty * actualAmount,
                            },
                            index,
                        );
                    }}
                    label="quantity"
                >
                    <Input
                        autoFocus
                        defaultValue={service.qty || '-'}
                        placeholder="quantity"
                        type="number"
                        min={0}
                    />
                </EditableCellV2>
            ),
        };
    };

    renderMrp = (service: Service) => {
        return <p>₹{formattedNumber(service.mrp)}</p>;
    };

    renderDiscount = (service: Service, __: Service, index: number) => {
        const { updateService } = this.props;

        return {
            children: (
                <EditableCellV2
                    fieldName={`treatment-service-discount-${index}`}
                    defaultValue={String(service.discount) || ''}
                    onBlur={(e) => {
                        const discount = Number(e.target.value);
                        if (discount < 0 || discount > 100) {
                            notification.error({
                                message: `Discount must be between 0 to 100`,
                                duration: 5,
                            });
                            return;
                        }
                        updateService(
                            {
                                ...service,
                                discount: discount,
                                amount: service.mrp * (1 - discount / 100),
                            },
                            index,
                        );
                    }}
                    label="discount"
                >
                    <Input
                        autoFocus
                        defaultValue={service.discount || '-'}
                        placeholder="discount"
                        type="number"
                        min={0}
                        max={100}
                    />
                </EditableCellV2>
            ),
        };
    };

    renderAmount = (service: Service) => {
        return <p>₹{formattedNumber(service.amount)}</p>;
    };

    renderRemarks = (service: Service, __: Service, index: number) => {
        const { updateService } = this.props;
        const value = service.remarks || '';

        return {
            children: (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        fieldName={`treatment-service-remarks-${index}`}
                        defaultValue={value}
                        onBlur={(e) => {
                            updateService({ ...service, remarks: e.target.value }, index);
                        }}
                        label="remarks"
                    >
                        <Input.TextArea
                            autoFocus
                            defaultValue={value}
                            rows={1}
                            placeholder="remarks"
                        />
                    </EditableCellV2>
                </div>
            ),
        };
    };

    renderAction = (_: Service, __: Service, index: number) => {
        const { deleteService } = this.props;

        return (
            <button onClick={() => deleteService(index)}>
                <svg
                    className="w-12 h-14 fill-darwin-neutrals-600"
                    viewBox="0 0 12 14"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M4.80664 1.34375C4.73047 1.34375 4.67969 1.39453 4.62891 1.44531L4.14648 2.15625H7.82812L7.3457 1.44531C7.32031 1.39453 7.24414 1.34375 7.16797 1.34375H4.80664ZM9.30078 2.15625H9.63086H10.875H11.0781C11.4082 2.15625 11.6875 2.43555 11.6875 2.76562C11.6875 3.12109 11.4082 3.375 11.0781 3.375H10.7734L10.1641 11.627C10.0879 12.4902 9.40234 13.125 8.53906 13.125H3.43555C2.57227 13.125 1.88672 12.4902 1.81055 11.627L1.20117 3.375H0.921875C0.566406 3.375 0.3125 3.12109 0.3125 2.76562C0.3125 2.43555 0.566406 2.15625 0.921875 2.15625H1.125H2.34375H2.67383L3.61328 0.759766C3.86719 0.378906 4.32422 0.125 4.80664 0.125H7.16797C7.65039 0.125 8.10742 0.378906 8.36133 0.759766L9.30078 2.15625ZM9.55469 3.375H2.41992L3.0293 11.5508C3.05469 11.7539 3.23242 11.9062 3.43555 11.9062H8.53906C8.74219 11.9062 8.91992 11.7539 8.94531 11.5508L9.55469 3.375Z" />
                </svg>
            </button>
        );
    };

    render(): ReactNode {
        const { services } = this.props;
        const defaultHeaderStyle = headerStyle().style;

        return (
            <Table
                dataSource={services}
                className="border border-darwin-neutral_new-200"
                pagination={false}
                id="treatment-services-table"
                bordered
                rowClassName={(_, index) => `editable-row estimate-services-table-row-${index}`}
            >
                <Column
                    title={this.seqHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-seq"
                    render={this.renderSeq}
                    className="whitespace-nowrap"
                />
                <Column
                    title={this.nameHeaderContent}
                    onHeaderCell={() => ({
                        style: {
                            ...defaultHeaderStyle,
                            width: '20%',
                        },
                    })}
                    key="treatment-service-row-name"
                    render={this.renderName}
                />
                <Column
                    title={this.teethHeaderContent}
                    onHeaderCell={() => ({
                        style: {
                            ...defaultHeaderStyle,
                            width: '15%',
                        },
                    })}
                    key="treatment-service-row-teeth"
                    render={this.renderTeeth}
                />
                <Column
                    title={this.regionHeaderContent}
                    onHeaderCell={() => ({
                        style: {
                            ...defaultHeaderStyle,
                            width: '15%',
                        },
                    })}
                    key="treatment-service-row-region"
                    render={this.renderRegion}
                />
                <Column
                    title={this.qtyHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-qty"
                    render={this.renderQty}
                    className="whitespace-nowrap"
                />
                <Column
                    title={this.mrpHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-mrp"
                    render={this.renderMrp}
                    className="whitespace-nowrap"
                />
                <Column
                    title={this.discountHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-discount"
                    render={this.renderDiscount}
                    className="whitespace-nowrap"
                />
                <Column
                    title={this.amountHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-amount"
                    render={this.renderAmount}
                    className="whitespace-nowrap"
                />
                <Column
                    title={this.remarksHeaderContent}
                    onHeaderCell={() => ({
                        style: {
                            ...defaultHeaderStyle,
                            width: '15%',
                        },
                    })}
                    key="treatment-service-row-remarks"
                    render={this.renderRemarks}
                />
                <Column
                    title={this.actionHeaderContent}
                    onHeaderCell={headerStyle}
                    key="treatment-service-row-action"
                    render={this.renderAction}
                    className="whitespace-nowrap"
                />
            </Table>
        );
    }
}

export default connect((state: GlobalState) => ({
    chartType: getChartType(state),
}))(EstimateServicesTable);
